import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import { useAtomValue } from "jotai";
import { initReactI18next } from "react-i18next";
import { getLocalPreferences, setLocalPreferences, userLocalPreferences } from "./state/preferences";

/**
 * Defines the list of languages supported by this application.
 */
export enum SupportedLanguages {
	English = "en",
	Italian = "it",
	Spanish = "es",
}

/**
 * Defines the list of languages supported by this application as an iterable array.
 */
export const SupportedLanguagesList = [
	SupportedLanguages.English,
	SupportedLanguages.Italian,
	SupportedLanguages.Spanish,
];

/**
 * Support hook for getting language used by the current user.
 * This is useful in various scenarios, for example when building query cache keys.
 *
 * If no user preference or browser language has been set, the result of this hook
 * falls back to SupportedLanguages.English.
 *
 * @returns the suggested language used by the current user
 */
export const useCurrentLanguage = (): string => {
	const preferences = useAtomValue(userLocalPreferences);

	return preferences.language ?? preferences.browserLanguage ?? SupportedLanguages.English;
};

// Resolve the initial language before init, so that only the right resources are fetched
// and no language switch happens after the first render.
const localPreferences = getLocalPreferences();
const browserLanguage = navigator.language.split("-")[0];
setLocalPreferences({ ...localPreferences, browserLanguage });

/**
 * Resolves once the initial language resources have been loaded.
 * The application must not be rendered before, otherwise translation keys are shown instead of labels.
 */
export const i18nReady = i18n
	.use(Backend)
	.use(initReactI18next)
	.init({
		lng: localPreferences.language ?? browserLanguage,
		ns: [
			"common",
			"errors",
			"navigation",
			"layoutTitles",
			"authentication",
			"format.numbers",
			"validation",
			"snackbar",
			"masterData",
			"dataIO",
			"dataInsights",
			"feature.account",
			"whatIf.carbonsnap",
			"feature.company",
			"feature.network",
			"feature.results",
			"feature.roles",
			"feature.units",
			"feature.users",
			"result.transport",
			"dataInsights.rootcause",
		],
		defaultNS: "common",
		fallbackLng: {
			"it-it": [SupportedLanguages.Italian],
			"es-es": [SupportedLanguages.Spanish],
			"en-us": [SupportedLanguages.English],
			default: [SupportedLanguages.English],
		},
		load: "languageOnly",
		keySeparator: "/",
		supportedLngs: ["en", "it", "it-it", "it-IT", "es", "es-es", "es-ES"],
		interpolation: {
			escapeValue: false, // Not needed for react as it escapes by default.
		},
		react: {
			transSupportBasicHtmlNodes: true,
			transKeepBasicHtmlNodesFor: ["br", "strong", "i", "b"],
			useSuspense: false,
		},
	});

// Extend dayjs so that it can format dates with localization support.
dayjs.extend(localizedFormat);

export default i18n;
